import type React from 'react';
import { useCallback, useRef } from 'react';

import { EntryStatusEnum } from '@clubspark-node/clubspark-tennis-types';
import Grid from '@mui/material/Grid';
import type { Row } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import RemoveIcon from 'assets/icons/icn-glyph-24-remove.svg?react';
import ToolsGrid, { ToolButton, ToolText } from 'components/tables/common/ToolsGrid';
import { useStyles } from 'components/tables/competitors/alternates/styles';
import useWithdrawDialog from 'containers/dialogs/WithdrawDialog';
import useMutateTournament from 'hooks/tournament/useMutateTournament';
import type { ParticipantInterface } from 'typedefs/store/tmxTypes';

const { WITHDRAWN } = EntryStatusEnum;

interface UnpairedBulkActionsProps<T extends Record<string, any>> {
  selectedFlatRows: Row<T>[];
  onResetRowSelection: () => void;
}

const UnpairedBulkActions: React.FC<React.PropsWithChildren<UnpairedBulkActionsProps<ParticipantInterface>>> = ({
  selectedFlatRows,
  onResetRowSelection,
}) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const { eventId } = useParams<{ eventId: string }>();
  const showWithdrawDecisionDialog = useWithdrawDialog();
  const [withdrawParticipantFromEvent] = useMutateTournament('withdrawParticipantFromEvent');
  const selectedIds = useRef<string[]>([]);
  selectedIds.current = selectedFlatRows.map((row) => row.id);

  const handleOnWithdrawal = useCallback(async () => {
    const withdrawDecision = await showWithdrawDecisionDialog(false, false, false);
    if (withdrawDecision) {
      withdrawParticipantFromEvent(selectedIds.current, WITHDRAWN, eventId);
      onResetRowSelection();
    }
  }, [eventId, onResetRowSelection, showWithdrawDecisionDialog, withdrawParticipantFromEvent]);

  return (
    !!selectedFlatRows.length && (
      <Grid
        container
        item
        alignItems="center"
        className={classes.participantTableActionContainer}
        justifyContent={'space-between'}
        spacing={2}
      >
        <Grid container item xs={12}>
          <ToolsGrid item>
            <ToolText>{selectedFlatRows.length} Selected:</ToolText>
            <ToolButton size="large" startIcon={<RemoveIcon />} onClick={handleOnWithdrawal}>
              {t('withdraw')}
            </ToolButton>
          </ToolsGrid>
        </Grid>
      </Grid>
    )
  );
};

export default UnpairedBulkActions;
